import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Autocomplete,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  cardSx,
  sectionHeaderSx,
  cardContentSx,
  flexWrapBoxSx,
  fieldBoxSx,
  selectSx,
  selectMenuPaperSx,
  textFieldSx,
  dateFieldSx,
  agreedPriceFieldSx,
  actionButtonsSx,
  reminderDialogTitleSx,
  reminderDialogContentSx,
  reminderDialogTextSx,
} from '../../../styles/TripInformationSection.styles';
import { Info as InfoIcon, Edit as EditIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { api } from '../../../../../services/api';
import {
  TravelService, ServiceFleetOption,
  ServiceOption,
  FleetOption,
  TripDetails,
  TripUpdatePayload,
  TripInformationSectionProps
} from '../../../../../types/tripDetailsInformation';
import { calculateDistance } from '../../../../../utils/distanceCalculator';
import { getPlacePredictions, initGooglePlacesApi, resetSessionToken } from '../../../../../utils/googlePlacesApi';
import { PlacePrediction, DestinationWithDistance } from '../../../../../types/tripInformationNewTrip';
import TripInformationHeader from './TripInformationHeader';
import TripPricingCalculator from '../../../../trips/TripPricingCalculator';


const TripInformationSection: React.FC<TripInformationSectionProps> = ({
  trip,
  onUpdate,
  loading = false,
  customer = null,
  disabled = false,
  isInProgress,
  isCompleted,
  isClosed
}) => {
  // WhatsApp notification states
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState(false);
  const [openReminderDialog, setOpenReminderDialog] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  // Remove isForSelf from state
  const [isEditing, setIsEditing] = useState(false);
  const [editingTrip, setEditingTrip] = useState<TripDetails>(trip);
  const [saving, setSaving] = useState(false);
  // Add ref for scroll position preservation
  const tripInfoSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  const [services, setServices] = useState<TravelService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | ''>('');
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [fleetOptions, setFleetOptions] = useState<FleetOption[]>([]);
  const [selectedFleetId, setSelectedFleetId] = useState<number | ''>('');
  const [pricingTable, setPricingTable] = useState<any>(null);
  const [calculatedDays, setCalculatedDays] = useState<number>(1);
  const [customKm, setCustomKm] = useState<number>(0);
  const [agreedPrice, setAgreedPrice] = useState<number | null>(null);
  const [currentDiscountedPrice, setCurrentDiscountedPrice] = useState<number>(0);
  const [distancePriceCalculation, setDistancePriceCalculation] = useState<number | null>(null);
  const [hasUserEditedPrice, setHasUserEditedPrice] = useState(false);

  // Add dynamic field state
  const [isMumbaiAirportService, setIsMumbaiAirportService] = useState(false);
  const [isOnewayService, setIsOnewayService] = useState(false);
  const [isPuneLocalService, setIsPuneLocalService] = useState(false);
  const [isPerPaxService,setIsPerPaxService] = useState(false);
  const [airportPickupDrop, setAirportPickupDrop] = useState<string>('');
  const [onewayRoute, setOnewayRoute] = useState<string>('');
  const [perpaxRoute, setPerpaxRoute] = useState<string>('');
  const [puneLocalRoute, setPuneLocalRoute] = useState<string>('');

  // Multiple destinations state
  const [destinationsList, setDestinationsList] = useState<string[]>([]);
  const [destinationsWithDistance, setDestinationsWithDistance] = useState<DestinationWithDistance[]>([]);
  const [newDestination, setNewDestination] = useState('');
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([]);
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [totalDistance, setTotalDistance] = useState<number | null>(null);

  // State for drag and drop
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [odometerErrors, setOdometerErrors] = useState<{
    startOdometer: string;
    pickUpOdometer: string;
    dropOffOdometer: string;
    endOdometer: string;
  }>({
    startOdometer: '',
    pickUpOdometer: '',
    dropOffOdometer: '',
    endOdometer: '',
  }); 

  // Load services when editing starts
  useEffect(() => {
    if (isEditing) {
      loadServices();
    }
  }, [isEditing]);

  // Update selected service when trip data changes and when services are loaded
  useEffect(() => {
    if (trip?.travelServiceDetailId && serviceOptions.length > 0) {
      // Calculate service index from travelServiceDetailId
      const travelServiceDetailId = Number(trip.travelServiceDetailId);
      const serviceIndex = Math.floor((travelServiceDetailId - 1) / 4);
      const calculatedServiceId = serviceIndex + 1;
      // Find service by calculated ID
      const serviceById = serviceOptions.find(s => s.id === calculatedServiceId);
      if (serviceById) {
        setSelectedServiceId(calculatedServiceId);
        return;
      }

      // Fallback: Find service by name matching
      const currentServiceName = getServiceName();
      const serviceByName = serviceOptions.find(s =>
        s.name?.toLowerCase().trim() === currentServiceName?.toLowerCase().trim()
      );

      if (serviceByName) {
        setSelectedServiceId(serviceByName.id);
        return;
      }

    } else if (trip?.travelServiceDetail?.travelService?.id && serviceOptions.length > 0) {
      const directServiceId = trip.travelServiceDetail.travelService.id;

      const serviceById = serviceOptions.find(s => s.id === directServiceId);
      if (serviceById) {
        setSelectedServiceId(directServiceId);
        return;
      }

      // Fallback: Find by name
      const currentServiceName = getServiceName();
      const serviceByName = serviceOptions.find(s =>
        s.name?.toLowerCase().trim() === currentServiceName?.toLowerCase().trim()
      );

      if (serviceByName) {
        setSelectedServiceId(serviceByName.id);
      }
    }
  }, [trip, serviceOptions]);

  // Update fleet selection when service options and trip data are available
  useEffect(() => {
    if (trip?.travelServiceDetailId && fleetOptions.length > 0) {
      const currentFleetOption = fleetOptions.find((f: FleetOption) => f.actualDatabaseId === Number(trip.travelServiceDetailId));
      if (currentFleetOption) {
        setSelectedFleetId(currentFleetOption.id);
      }
    }

  }, [trip, fleetOptions]);

  useEffect(() => {
    const selectedVehicle = fleetOptions.find((f: FleetOption) =>
      f.displayName.includes(getVehicleName())
    );
    if (selectedVehicle && !selectedFleetId) {
      setSelectedFleetId(selectedVehicle.id);
    
    if (isEditing) {
      setEditingTrip(prev => ({
        ...prev,
        travelServiceDetailId: selectedVehicle.actualDatabaseId
      }));
    }
  }
  }, [fleetOptions,isEditing]);

  // Update fleet options when service is selected
  useEffect(() => {
    if (selectedServiceId && pricingTable?.data) {
      const serviceIndex = Number(selectedServiceId) - 1;
      const selectedService = pricingTable.data[serviceIndex];
      const selectedServiceOption = serviceOptions.find(s => s.id === Number(selectedServiceId));

      if (selectedService?.carOptions && Array.isArray(selectedService.carOptions) && selectedServiceOption) {
        const fleetOpts = selectedService.carOptions.map((fleet: any, fleetIndex: number) => {
          const carType = fleet.carType || 'Vehicle';
          const makeModel = fleet.makeModel || fleet.makeAndModel || fleet.model || fleet.name || 'Model';
          const capacity = fleet.capacity || 'N/A';

          return {
            id: fleetIndex + 1, // Display ID
            fleetIndex: fleetIndex, // Keep track of array index
            actualDatabaseId: fleet.id || fleet.travelServiceDetailId, // Use the actual database ID from API
            displayName: fleet.slot && fleet.slot !== ''
              ? `${carType} | ${makeModel} | ${capacity} (${fleet.slot})`
              : `${carType} | ${makeModel} | ${capacity}`,
            fleetDetails: fleet,
            slot: fleet.slot
          };
        });
        setFleetOptions(fleetOpts);

        // Try to maintain selected fleet if it exists in new options
        if (editingTrip?.travelServiceDetailId) {
          const currentFleetOption = fleetOpts.find((f: FleetOption) => f.actualDatabaseId === Number(editingTrip.travelServiceDetailId));
          if (currentFleetOption) {
            setSelectedFleetId(currentFleetOption.id);
          } else {
            setSelectedFleetId('');
          }
        }
      } else {
        setFleetOptions([]);
        setSelectedFleetId('');
      }
    } else {
      setFleetOptions([]);
      setSelectedFleetId('');
    }
  }, [selectedServiceId, pricingTable, serviceOptions, editingTrip?.travelServiceDetailId]);

  // Update service-specific states when selected service changes during editing
  // Note: This useEffect ensures service-specific states are updated even when
  // selectedServiceId changes through other means (like initialization)
  useEffect(() => {
    if (isEditing && selectedServiceId && serviceOptions.length > 0) {
      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
      if (selectedService) {
        const serviceName = selectedService.name?.toLowerCase() || '';
        const isMumbaiAirport = serviceName.includes('mumbai airport') || serviceName.includes('airport');
        const isOneway = serviceName.includes('oneway') || serviceName.includes('one way');
        const isPuneLocal = serviceName.includes('pune local');
        const isPerPax = serviceName.includes('per pax');

        // Only update if different from current state to avoid unnecessary re-renders
        if (isMumbaiAirportService !== isMumbaiAirport) {
          setIsMumbaiAirportService(isMumbaiAirport);
        }
        if (isOnewayService !== isOneway) {
          setIsOnewayService(isOneway);
        }
        if(isPerPaxService !== isPerPax){
          setIsPerPaxService(isPerPax)
        }
        if (isPuneLocalService !== isPuneLocal) {
          setIsPuneLocalService(isPuneLocal);
        }

        // Clear destinations for services with predetermined routes (except Pune Local)
        if (isMumbaiAirport || isOneway || isPuneLocalService) {
          setDestinationsList([]);
          setDestinationsWithDistance([]);
          setTotalDistance(null);
        }

        // Reset dynamic field values when service type changes
        if (!isMumbaiAirport && airportPickupDrop) {
          setAirportPickupDrop('');
        }
        if (!isOneway && onewayRoute) {
          setOnewayRoute('');
        }
        if(!isPerPax && perpaxRoute){
          setPerpaxRoute('')
        }
        if (!isPuneLocal && puneLocalRoute) {
          setPuneLocalRoute('');
        }
      }
    }
  }, [selectedServiceId, serviceOptions, isEditing, isMumbaiAirportService, isOnewayService, isPuneLocalService, airportPickupDrop, onewayRoute, puneLocalRoute, isPerPaxService]);

  // Initialize Google Places API
  useEffect(() => {
    initGooglePlacesApi().catch((error: Error) => {
      console.error('Failed to initialize Google Places API:', error);
    });
  }, []);

  // Initialize destinations list from trip data
  useEffect(() => {
    const currentTrip = isEditing ? editingTrip : trip;
    if (currentTrip?.destinationCities && destinationsList.length === 0) {
      let destinations: string[] = [];

      if (currentTrip.destinationCities.includes(' | ')) {
        // Split by pipe separator for multiple destinations
        destinations = currentTrip.destinationCities.split(' | ').map(dest => dest.trim()).filter(dest => dest);
      } else {
        // For single destination, don't split by comma (it might be "City, State, Country" format)
        // Only split by comma if there are clear multiple destinations (check for common patterns)
        const commaCount = (currentTrip.destinationCities.match(/,/g) || []).length;
        const hasMultipleDestinations = commaCount > 2 || // More than "City, State, Country" format
          currentTrip.destinationCities.includes(', to ') || // Contains "to" indicating multiple destinations
          currentTrip.destinationCities.includes(' and '); // Contains "and" indicating multiple destinations

        if (hasMultipleDestinations) {
          destinations = currentTrip.destinationCities.split(',').map(dest => dest.trim()).filter(dest => dest);
        } else {
          // Treat as single destination
          destinations = [currentTrip.destinationCities.trim()];
        }
      }

      if (destinations.length > 0) {
        setDestinationsList(destinations);
      }
    }
  }, [trip.destinationCities, editingTrip.destinationCities, isEditing]);

  // Fetch place predictions when newDestination changes
  useEffect(() => {
    const fetchPredictions = async () => {
      // Only fetch predictions for Outstation Round Trip service
      if (newDestination.length > 2 && !isMumbaiAirportService && !isOnewayService && !isPuneLocalService && !isPerPaxService) {
        // Check if it's specifically Outstation Round Trip
        const isOutstationRoundTrip = isEditing && selectedServiceId && serviceOptions.length > 0 ?
          (() => {
            const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
            const serviceName = selectedService?.name?.toLowerCase() || '';
            return serviceName.includes('outstation') && serviceName.includes('round trip');
          })() :
          (() => {
            const serviceName = trip?.travelServiceDetail?.travelService?.name?.toLowerCase() || '';
            return serviceName.includes('outstation') && serviceName.includes('round trip');
          })();

        if (isOutstationRoundTrip) {
          try {
            setIsLoadingPredictions(true);
            const predictions = await getPlacePredictions(newDestination);
            setPlacePredictions(predictions);
          } catch (error) {
            console.error('Error fetching place predictions:', error);
            setPlacePredictions([]);
          } finally {
            setIsLoadingPredictions(false);
          }
        } else {
          setPlacePredictions([]);
          setIsLoadingPredictions(false);
        }
      } else {
        setPlacePredictions([]);
        setIsLoadingPredictions(false); // Ensure loading state is reset when conditions are not met
      }
    };

    const timer = setTimeout(fetchPredictions, 300); // Debounce API calls
    return () => clearTimeout(timer);
  }, [newDestination, isMumbaiAirportService, isOnewayService, isPuneLocalService, isPerPaxService, selectedServiceId, serviceOptions, isEditing]);

  // Calculate distances when source city or destinations change
  // Only calculate for Outstation Round Trip service
  useEffect(() => {
    // Check if it's specifically Outstation Round Trip
    const isOutstationRoundTrip = isEditing && selectedServiceId && serviceOptions.length > 0 ?
      (() => {
        const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
        const serviceName = selectedService?.name?.toLowerCase() || '';
        return serviceName.includes('outstation') && serviceName.includes('round trip');
      })() :
      (() => {
        const serviceName = trip?.travelServiceDetail?.travelService?.name?.toLowerCase() || '';
        return serviceName.includes('outstation') && serviceName.includes('round trip');
      })();

    if (isOutstationRoundTrip) {
      calculateDistances();
    }
  }, [isEditing ? editingTrip.sourceCity : trip.sourceCity, destinationsList, selectedServiceId, serviceOptions, isEditing, editingTrip?.travelServiceDetail?.travelService?.name, trip?.travelServiceDetail?.travelService?.name]);

  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const data = await api.getPricingTable() as any;
      setPricingTable(data);

      // Create service options with proper ID mapping
      if (data?.data && Array.isArray(data.data)) {
        const services = data.data.map((service: any, serviceIndex: number) => {
          return {
            id: serviceIndex + 1, // Display ID
            serviceIndex: serviceIndex, // Keep track of array index
            name: service.serviceName || `Service ${serviceIndex + 1}`,
            carOptions: service.carOptions || []
          };
        });
        setServiceOptions(services);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to determine if end date should be hidden based on service type
  const shouldHideEndDate = () => {
    // When editing, check the selected service
    if (isEditing && selectedServiceId) {
      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
      if (selectedService) {
        const serviceName = selectedService.name?.toLowerCase() || '';
        // Hide end date for airport, oneway, and pune local services
        return isMumbaiAirportService ||
        isPerPaxService ||
          isOnewayService ||
          serviceName.includes('pune local') ||
          serviceName.includes('airport') ||
          serviceName.includes('oneway') ||
          serviceName.includes('per pax') ||
          serviceName.includes('one way');
      }
    }

    // When not editing, check the trip's service type
    if (trip?.travelServiceDetail) {
      const serviceType = trip.travelServiceDetail.serviceType?.toLowerCase() || '';
      const serviceName = trip.travelServiceDetail.travelService?.name?.toLowerCase() || '';

      // Hide end date for airport, oneway, and pune local services
      return serviceName.includes('pune local') ||
        serviceName.includes('airport') ||
        serviceName.includes('oneway') ||
        serviceName.includes('one way') ||
        serviceType.includes('pune local') ||
        serviceType.includes('airport') ||
        serviceType.includes('oneway') ||
        serviceType.includes('per pax') ||
        serviceType.includes('one way');
    }
    return true;
  };

  // Helper function to determine if "To" field should be hidden
  const shouldHideToField = () => {
    // When editing, check multiple sources for Pune Local service
    if (isEditing) {
      // First check the selected service if available
      if (selectedServiceId) {
        const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
        if (selectedService) {
          const serviceName = selectedService.name?.toLowerCase() || '';
          return serviceName.includes('pune local');
        }
      }

      // If no selectedServiceId yet, check the original trip data
      if (trip?.travelServiceDetail) {
        const serviceType = trip.travelServiceDetail.serviceType?.toLowerCase() || '';
        const serviceName = trip.travelServiceDetail.travelService?.name?.toLowerCase() || '';
        return serviceName.includes('pune local') || serviceType.includes('pune local');
      }

      // Also check the isPuneLocalService state
      return isPuneLocalService;
    }

    // When not editing, check the trip's service type
    if (!isEditing && trip?.travelServiceDetail) {
      const serviceType = trip.travelServiceDetail.serviceType?.toLowerCase() || '';
      const serviceName = trip.travelServiceDetail.travelService?.name?.toLowerCase() || '';

      return serviceName.includes('pune local') || serviceType.includes('pune local');
    }

    return false;
  };

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    const updatedTrip = { ...editingTrip };

    if (keys.length === 1) {
      (updatedTrip as any)[keys[0]] = value;
    } else if (keys.length === 2) {
      (updatedTrip as any)[keys[0]] = {
        ...(updatedTrip as any)[keys[0]],
        [keys[1]]: value
      };
    }
    setEditingTrip(updatedTrip);
  };
  const handleModify = () => {
    setIsEditing(true);
    setEditingTrip({ ...trip });

    // Reset loading states when entering edit mode
    setIsLoadingPredictions(false);
    setPlacePredictions([]);
    setNewDestination('');

    // Initialize destinations list from trip data
    const serviceName = trip?.travelServiceDetail?.travelService?.name?.toLowerCase() || '';
    const isMumbaiAirport = serviceName.includes('mumbai airport') || serviceName.includes('airport');
    const isOneway = serviceName.includes('oneway') || serviceName.includes('one way');
    const isPuneLocal = serviceName.includes('pune local');
    const isPerPax = serviceName.includes('per pax');
    const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

    // Set service-specific states immediately when entering edit mode
    setIsMumbaiAirportService(isMumbaiAirport);
    setIsOnewayService(isOneway);
    setIsPerPaxService(isPerPax);
    setIsPuneLocalService(isPuneLocal);

    // Only initialize destinations for Outstation Round Trip service
    if (isOutstationRoundTrip && trip?.destinationCities) {
      let destinations: string[] = [];

      if (trip.destinationCities.includes(' | ')) {
        // Split by pipe separator for multiple destinations
        destinations = trip.destinationCities.split(' | ').map(dest => dest.trim()).filter(dest => dest);
      } else {
        // For single destination, don't split by comma (it might be "City, State, Country" format)
        // Only split by comma if there are clear multiple destinations (check for common patterns)
        const commaCount = (trip.destinationCities.match(/,/g) || []).length;
        const hasMultipleDestinations = commaCount > 2 || // More than "City, State, Country" format
          trip.destinationCities.includes(', to ') || // Contains "to" indicating multiple destinations
          trip.destinationCities.includes(' and '); // Contains "and" indicating multiple destinations

        if (hasMultipleDestinations) {
          destinations = trip.destinationCities.split(',').map(dest => dest.trim()).filter(dest => dest);
        } else {
          // Treat as single destination
          destinations = [trip.destinationCities.trim()];
        }
      }

      setDestinationsList(destinations);
    } else {
      // Clear destinations for all other services
      setDestinationsList([]);
      setDestinationsWithDistance([]);
      setTotalDistance(null);
    }

    // Prefill agreed price from trip pricing data
    if (trip?.pricing?.pricing?.agreedTripFare && trip.pricing.pricing.agreedTripFare > 0) {
      // Use the existing agreed trip fare from pricing data
      setAgreedPrice(trip.pricing.pricing.agreedTripFare);
    } else {
      // If no pricing data available, reset to null
      setAgreedPrice(null);
    }
    setCurrentDiscountedPrice(0);

    if (trip?.travelServiceDetail?.travelService?.name) {
      const serviceName = trip.travelServiceDetail.travelService.name.toLowerCase();

      // Set up Pune Local route automatically
      if (isPuneLocal) {
        setPuneLocalRoute('within-pune'); // Always set the route for Pune Local
        // Ensure source city is set to Pune for local service
        if (!trip.sourceCity || trip.sourceCity.toLowerCase() !== 'pune') {
          setEditingTrip(prev => ({
            ...prev,
            sourceCity: 'Pune'
          }));
        }
      }

      // Try to determine pickup/drop or route from source/destination
      if (isMumbaiAirport) {
        if (trip.sourceCity?.toLowerCase().includes('mumbai') && trip.destinationCities?.toLowerCase().includes('pune')) {
          setAirportPickupDrop('pickup');
        } else if (trip.sourceCity?.toLowerCase().includes('pune') && trip.destinationCities?.toLowerCase().includes('mumbai')) {
          setAirportPickupDrop('drop');
        }
      }

      if (isOneway) {
        if (trip.sourceCity?.toLowerCase().includes('pune') && trip.destinationCities?.toLowerCase().includes('mumbai')) {
          setOnewayRoute('pune-to-mumbai');
        } else if (trip.sourceCity?.toLowerCase().includes('mumbai') && trip.destinationCities?.toLowerCase().includes('pune')) {
          setOnewayRoute('mumbai-to-pune');
        }
      }
      if (isPerPax) {
        if (trip.sourceCity?.toLowerCase().includes('pune') && trip.destinationCities?.toLowerCase().includes('mumbai')) {
          setPerpaxRoute('pune-to-mumbai');
        } else if (trip.sourceCity?.toLowerCase().includes('mumbai') && trip.destinationCities?.toLowerCase().includes('pune')) {
          setPerpaxRoute('mumbai-to-pune');
        }
      }
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditingTrip(trip);

    // Reset agreed price and discounted price
    setAgreedPrice(null);
    setCurrentDiscountedPrice(0);

    // Reset dynamic field states
    setIsMumbaiAirportService(false);
    setIsOnewayService(false);
    setIsPerPaxService(false);
    setIsPuneLocalService(false);
    setAirportPickupDrop('');
    setOnewayRoute('');
    setPerpaxRoute('');
    setPuneLocalRoute('');

    // Reset destinations list
    setDestinationsList([]);
    setDestinationsWithDistance([]);
    setNewDestination('');
    setTotalDistance(null);

    // Reset selections to original trip values
    if (trip?.travelServiceDetailId && serviceOptions.length > 0) {
      const travelServiceDetailId = Number(trip.travelServiceDetailId);
      const serviceIndex = Math.floor((travelServiceDetailId - 1) / 4);
      setSelectedServiceId(serviceIndex + 1);

      // Reset fleet selection too
      const currentFleetOption = fleetOptions.find((f: FleetOption) => f.actualDatabaseId === travelServiceDetailId);
      if (currentFleetOption) {
        setSelectedFleetId(currentFleetOption.id);
      }
    }
  };

  // Handle opening WhatsApp notification dialog
  const handleOpenWhatsAppDialog = () => {
    if (!customer?.mobileNumber) {
      setSnackbarMessage('Customer mobile number is required to send WhatsApp notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setOpenWhatsAppDialog(true);
  };

  // Handle closing WhatsApp notification dialog
  const handleCloseWhatsAppDialog = () => {
    setOpenWhatsAppDialog(false);
  };

  // Handle sending WhatsApp notification
  const handleSendWhatsAppNotification = async () => {
    if (!customer?.mobileNumber || !trip) return;

    setSendingNotification(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
      // Format the mobile number (remove any non-digit characters)
      let mobileNumber = customer.mobileNumber.replace(/\D/g, '');
      let countryCode = customer?.countryCode || '91';
      mobileNumber = mobileNumber.startsWith(countryCode) 
        ? mobileNumber.slice(countryCode.length) 
        : mobileNumber;

      // Prepare notification data for trip_booking_confirmation template
      const notificationData = {
        templateName: 'trip_booking_confirmation',
        customerCountryCode: countryCode, 
        customerNumber: mobileNumber,
        body: {
          name: customer.name || 'Customer',
          tripcode: trip.tripCode || '',
          pickuplocation: trip.sourceCity || '',
          droplocation: trip.destinationCities || '',
          tripdate: formatDate(trip.startDatetime).split(',')[0] || ''
        }
      };

      // Send the notification
      await api.sendWhatsAppNotification(notificationData);

      setSnackbarMessage('WhatsApp notification sent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenWhatsAppDialog(false);
    } catch (error: any) {
      console.error('Error sending WhatsApp notification:', error);
      setSnackbarMessage(error.message || 'Failed to send WhatsApp notification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSendingNotification(false);
    }
  };

  // Handle opening WhatsApp reminder dialog
  const handleOpenReminderDialog = () => {
    if (!customer?.mobileNumber) {
      setSnackbarMessage('Customer mobile number is required to send WhatsApp reminder');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setOpenReminderDialog(true);
  };

  // Handle closing WhatsApp reminder dialog
  const handleCloseReminderDialog = () => {
    setOpenReminderDialog(false);
  };

  // Handle sending WhatsApp reminder
  const handleSendWhatsAppReminder = async () => {
    if (!customer?.mobileNumber || !trip) return;

    setSendingReminder(true);
    setShouldPreserveScroll(true); // Set flag to preserve scroll
    try {
      // Format the mobile number (remove any non-digit characters)
       let mobileNumber = customer.mobileNumber.replace(/\D/g, '');
       let countryCode = customer?.countryCode || '91';
        mobileNumber = mobileNumber.startsWith(countryCode) 
          ? mobileNumber.slice(countryCode.length) 
          : mobileNumber;

      // Prepare notification data for trip_reminder template
      const notificationData = {
        templateName: 'trip_reminder', // You'll need to create this template
        customerCountryCode: countryCode, // Default to India
        customerNumber: mobileNumber,
        body: {
          name: customer.name || 'Customer',
          tripcode: trip.tripCode || '',
          pickuplcoation: trip.sourceCity || '',
          droplocation: trip.destinationCities || '',
          datetime: formatDate(trip.startDatetime).split(',')[0] || '',
          triptime: formatDate(trip.startDatetime).split(',')[1]?.trim() || ''
        }
      };

      // Send the reminder notification
      await api.sendWhatsAppNotification(notificationData);

      setSnackbarMessage('WhatsApp trip reminder sent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenReminderDialog(false);
    } catch (error: any) {
      console.error('Error sending WhatsApp reminder:', error);
      setSnackbarMessage(error.message || 'Failed to send WhatsApp reminder');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSendingReminder(false);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    const selectedService = serviceOptions.find(s => s.id === Number(serviceId));
    if (selectedService) {
      setSelectedServiceId(Number(serviceId));
      setSelectedFleetId(''); // Reset fleet selection when service changes

      // Keep agreed price as user entered, don't reset
      setCurrentDiscountedPrice(0);

      // Reset From and To fields when service type changes
      setEditingTrip(prev => ({
        ...prev,
        sourceCity: '',
        destinationCities: ''
      }));

      // Determine service-specific features
      const serviceName = selectedService.name?.toLowerCase() || '';
      const isMumbaiAirport = serviceName.includes('mumbai airport') || serviceName.includes('airport');
      const isOneway = serviceName.includes('oneway') || serviceName.includes('one way');
      const isPuneLocal = serviceName.includes('pune local');
      const isPerPax = serviceName.includes('per pax');

      setIsMumbaiAirportService(isMumbaiAirport);
      setIsOnewayService(isOneway);
      setIsPerPaxService(isPerPax);
      setIsPuneLocalService(isPuneLocal);

      // Clear destinations list for ALL services when changing
      setDestinationsList([]);
      setDestinationsWithDistance([]);
      setTotalDistance(null);

      // For Pune Local, set default source city after clearing
      if (isPuneLocal) {
        setPuneLocalRoute('within-pune'); // Set default route
        setEditingTrip(prev => ({
          ...prev,
          sourceCity: 'Pune',
          destinationCities: ''
        }));
      }

      // Reset dynamic field values when service changes
      if (!isMumbaiAirport) {
        setAirportPickupDrop('');
      }
      if (!isOneway) {
        setOnewayRoute('');
      }
      if(!isPerPax){
        setPerpaxRoute('')
      }
      if (!isPuneLocal) {
        setPuneLocalRoute('');
      }

      // Clear the travelServiceDetailId since we need both service and fleet
      setEditingTrip(prev => ({
        ...prev,
        travelServiceDetailId: undefined,
        travelServiceDetail: {
          id: undefined,
          travelService: {
            id: selectedService.id,
            name: selectedService.name
          }
        }
      }));
    }
  };

  const handleFleetChange = (fleetId: string) => {
    const selectedFleet = fleetOptions.find(f => f.id === Number(fleetId));
    const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));

    if (selectedFleet && selectedService) {
      setSelectedFleetId(Number(fleetId));

      // Keep agreed price as user entered, don't reset
      setCurrentDiscountedPrice(0);

      setEditingTrip(prev => ({
        ...prev,
        travelServiceDetailId: selectedFleet.actualDatabaseId,
        travelServiceDetail: {
          id: selectedFleet.actualDatabaseId,
          travelService: {
            id: selectedService.id,
            name: selectedService.name
          }
        }
      }));
    }
  };

  // Dynamic field handlers
  const handleAirportPickupDropChange = (selection: string) => {
    setAirportPickupDrop(selection);

    // Clear destinations list for Mumbai Airport service
    setDestinationsList([]);
    setDestinationsWithDistance([]);
    setTotalDistance(null);

    if (selection === 'pickup') {
      setEditingTrip(prev => ({
        ...prev,
        sourceCity: 'Mumbai Airport',
        destinationCities: 'Pune'
      }));
    } else if (selection === 'drop') {
      setEditingTrip(prev => ({
        ...prev,
        sourceCity: 'Pune',
        destinationCities: 'Mumbai Airport'
      }));
    }
  };

  const handleRouteChange = (selection: string) => {
    setOnewayRoute(selection);
    setPerpaxRoute(selection);

    // Clear destinations list for Oneway service
    setDestinationsList([]);
    setDestinationsWithDistance([]);
    setTotalDistance(null);

    if (selection === 'pune-to-mumbai') {
      setEditingTrip(prev => ({
        ...prev,
        sourceCity: 'Pune',
        destinationCities: 'Mumbai'
      }));
    } else if (selection === 'mumbai-to-pune') {
      setEditingTrip(prev => ({
        ...prev,
        sourceCity: 'Mumbai',
        destinationCities: 'Pune'
      }));
    }
  };



  // Calculate distances between source city and destinations
  const calculateDistances = async () => {
    const sourceCity = isEditing ? editingTrip.sourceCity : trip.sourceCity;
    if (!sourceCity || destinationsList.length === 0) {
      setDestinationsWithDistance([]);
      setTotalDistance(null);
      return;
    }

    // Initialize destinations with distance calculation state
    const newDestinationsWithDistance = destinationsList.map(dest => ({
      name: dest,
      distance: null as number | null,
      isCalculating: true
    }));

    setDestinationsWithDistance(newDestinationsWithDistance);

    // Calculate distance for each destination
    let sumDistance = 0;
    let lastPoint = sourceCity;

    for (let i = 0; i < destinationsList.length; i++) {
      try {
        const distance = await calculateDistance(lastPoint, destinationsList[i]);

        // Update the distance for this destination
        newDestinationsWithDistance[i] = {
          ...newDestinationsWithDistance[i],
          distance: distance,
          isCalculating: false
        };

        setDestinationsWithDistance([...newDestinationsWithDistance]);

        // Add to total and update last point for next calculation
        sumDistance += distance;
        lastPoint = destinationsList[i];
      } catch (error) {
        console.error(`Error calculating distance to ${destinationsList[i]}:`, error);

        // Mark as failed but don't block other calculations
        newDestinationsWithDistance[i] = {
          ...newDestinationsWithDistance[i],
          isCalculating: false
        };

        setDestinationsWithDistance([...newDestinationsWithDistance]);
      }
    }

    setTotalDistance(sumDistance);
  };

  // Function to add a new destination
  const handleAddDestination = () => {
    if (newDestination && !destinationsList.includes(newDestination)) {
      const newDestinations = [...destinationsList, newDestination];
      setDestinationsList(newDestinations);
      setNewDestination('');

      // Update the editing trip with the new destinations
      const destinationsString = newDestinations.join(' | ');
      handleInputChange('destinationCities', destinationsString);
    }
  };

  // Function to remove a destination
  const handleRemoveDestination = (index: number) => {
    const newDestinations = [...destinationsList];
    newDestinations.splice(index, 1);
    setDestinationsList(newDestinations);

    // Update the editing trip with the updated destinations
    const destinationsString = newDestinations.join(' | ');
    handleInputChange('destinationCities', destinationsString);
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.stopPropagation();
    setDraggedItemIndex(index);

    if (e.dataTransfer.setDragImage) {
      e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
    }

    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';

    e.currentTarget.style.opacity = '0.4';
    e.currentTarget.style.cursor = 'grabbing';
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drag end
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.cursor = 'grab';
    e.currentTarget.style.backgroundColor = '';

    setDraggedItemIndex(null);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.style.backgroundColor = '';

    if (draggedItemIndex === null || draggedItemIndex === dropIndex) {
      return;
    }

    const reorderedDestinations = [...destinationsList];
    const [movedDestination] = reorderedDestinations.splice(draggedItemIndex, 1);
    reorderedDestinations.splice(dropIndex, 0, movedDestination);

    setDestinationsList(reorderedDestinations);

    // Update the editing trip with the reordered destinations
    const destinationsString = reorderedDestinations.join(' | ');
    handleInputChange('destinationCities', destinationsString);

    // Recalculate distances with the new order
    calculateDistances();

    setDraggedItemIndex(null);
  };

  // Handle drag enter
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedItemIndex !== null) {
      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
    }
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.style.backgroundColor = '';
  };

  // Update calculated days when trip dates change
  useEffect(() => {
    // Helper to check if service is Outstation
    const isOutstation = () => {
      if (isEditing && selectedServiceId) {
        const service = serviceOptions.find(s => s.id === Number(selectedServiceId));
        return service?.name?.toLowerCase().includes('outstation') || false;
      }
      const serviceName = trip?.travelServiceDetail?.travelService?.name?.toLowerCase() || '';
      return serviceName.includes('outstation');
    };

    // Calculate days only for Outstation, else use 1
    if (isOutstation()) {
      const startDate = editingTrip.startDatetime || trip.startDatetime;
      const endDate = editingTrip.endDatetime || trip.endDatetime;
      if (startDate && endDate) {
        setCalculatedDays(calculateDays(startDate, endDate));
      } else {
        setCalculatedDays(1);
      }
    } else {
      setCalculatedDays(1);
    }
  }, [editingTrip.startDatetime, editingTrip.endDatetime, trip.startDatetime, trip.endDatetime, isEditing, selectedServiceId, serviceOptions, trip?.travelServiceDetail]);


  const selectedFleetOption = fleetOptions.find(f => f.id === Number(selectedFleetId));
  const estimatedPrice = ( calculatedDays * (selectedFleetOption?.fleetDetails?.offerPrice)) + (calculatedDays * (selectedFleetOption?.fleetDetails?.driverAllowance || 0));
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setShouldPreserveScroll(true); // Set flag to preserve scroll

      // Validate vehicle type selection
      if (!selectedFleetId || selectedFleetId === 0) {
        setSnackbarMessage('Please select a vehicle type');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setShouldPreserveScroll(false);
        return;
      }

      // Validate agreed trip price
      if (agreedPrice === null || agreedPrice === undefined || agreedPrice <= 0) {
        setSnackbarMessage('Please enter a valid agreed trip fare amount');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setShouldPreserveScroll(false);
        return;
      }

      if(isMumbaiAirportService){

         if ((!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') || 
            (!editingTrip.destinationCities || editingTrip.destinationCities.trim() === '')) {
          // If From/To are empty, then dropdown selection is required
          if (!airportPickupDrop || airportPickupDrop.trim() === '') {
            setSnackbarMessage('Please select Pickup or Drop for Mumbai Airport service');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
        }

        if (!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') {
          setSnackbarMessage('Source city is required for Mumbai Airport service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
        if(!editingTrip.destinationCities || editingTrip.destinationCities.trim() === ''){
          setSnackbarMessage('Destination city is required for Mumbai Airport service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
      }

      if (isOnewayService) {

        if ((!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') || 
          (!editingTrip.destinationCities || editingTrip.destinationCities.trim() === '')) {
          // If From/To are empty, then route selection is required
          if (!onewayRoute || onewayRoute.trim() === '') {
            setSnackbarMessage('Please select a route for Oneway service');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
        }

        if (!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') {
          setSnackbarMessage('Source city is required for Oneway service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
        
        if (!editingTrip.destinationCities || editingTrip.destinationCities.trim() === '') {
          setSnackbarMessage('Destination city is required for Oneway service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
      }

      if(isPerPaxService){
        if(editingTrip?.linkedTripCode?.trim() === ''){
          setSnackbarMessage('Please enter the linked trip code for Per Pax service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
        if(isPerPaxService){

          if ((!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') ||
            (!editingTrip.destinationCities || editingTrip.destinationCities.trim() === '')) {
            // If From/To are empty, then route selection is required
            if (!perpaxRoute || perpaxRoute.trim() === '') {
              setSnackbarMessage('Please select a route for Per Pax service');
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
              setShouldPreserveScroll(false);
              return;
            }
          }
          if (!editingTrip.sourceCity || editingTrip.sourceCity.trim() === '') {
            setSnackbarMessage('Source city is required for Per Pax service');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
          if (!editingTrip.destinationCities || editingTrip.destinationCities.trim() === '') {
            setSnackbarMessage('Destination city is required for Per Pax service');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
        }
      }

    // Validate end date for outstation service
    if (selectedServiceId && serviceOptions.length > 0) {
      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
      const serviceName = selectedService?.name?.toLowerCase() || '';
      const isOutstationService = serviceName.includes('round trip');
      
      if (isOutstationService) {
        // Check if end datetime is provided
        if (!editingTrip?.endDatetime) {
          setSnackbarMessage('End date and time is required for outstation service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }
        
        if (!editingTrip?.sourceCity || editingTrip.sourceCity.trim() === '') {
        setSnackbarMessage('Source city is required for outstation service');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setShouldPreserveScroll(false);
        return;
        }

         // Validate destination cities - must have at least one destination
        if (!editingTrip?.destinationCities || editingTrip.destinationCities.trim() === '' || destinationsList.length === 0) {
          setSnackbarMessage('Please add at least one destination city for outstation service');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setShouldPreserveScroll(false);
          return;
        }

        
        // Validate that end datetime is not before start datetime and meets 6-hour requirement
        if (editingTrip?.startDatetime && editingTrip?.endDatetime) {
          const startDate = new Date(editingTrip.startDatetime);
          const endDate = new Date(editingTrip.endDatetime);
          
          // Check if end date is before start date
          if (endDate < startDate) {
            setSnackbarMessage('End date & time cannot be before start date & time');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
          
          // Validate that end datetime is at least 6 hours after start datetime
          const diffInMilliseconds = endDate.getTime() - startDate.getTime();
          const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
          
          if (diffInHours < 6) {
            setSnackbarMessage('End date & time must be at least 6 hours after start date & time');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setShouldPreserveScroll(false);
            return;
          }
        }
      }
    }
       // Validate odometer readings
    const start = Number(editingTrip.startOdometer || 0);
    const pickup = Number(editingTrip.pickUpOdometer || 0);
    const dropoff = Number(editingTrip.dropOffOdometer || 0);
    const end = Number(editingTrip.endOdometer || 0);

    const odometerValidationErrors = {
      startOdometer: '',
      pickUpOdometer: '',
      dropOffOdometer: '',
      endOdometer: '',
    };

     // Check if current service is Per Pax to determine linkedTripCode value
    let linkedTripCodeValue = '';
    if (isPerPaxService) {
      linkedTripCodeValue = editingTrip.linkedTripCode || '';
    }

    let hasOdometerErrors = false;

    // Only validate if values exist (greater than 0)
    if (start > 0 && pickup > 0 && start > pickup) {
      odometerValidationErrors.startOdometer = 'Start odometer must be less than pickup odometer';
      odometerValidationErrors.pickUpOdometer = 'Pickup odometer must be greater than start odometer';
      hasOdometerErrors = true;
    }
    
    if (pickup > 0 && dropoff > 0 && pickup > dropoff) {
      odometerValidationErrors.pickUpOdometer = 'Pickup odometer must be less than dropoff odometer';
      odometerValidationErrors.dropOffOdometer = 'Dropoff odometer must be greater than pickup odometer';
      hasOdometerErrors = true;
    }
    
    if (dropoff > 0 && end > 0 && dropoff > end) {
      odometerValidationErrors.dropOffOdometer = 'Dropoff odometer must be less than end odometer'
      odometerValidationErrors.endOdometer = 'End odometer must be greater than dropoff odometer';
      hasOdometerErrors = true;
    }

    if (start > 0 && end > 0 && start > end) {
      odometerValidationErrors.startOdometer = 'Start odometer must be less than end odometer';
      odometerValidationErrors.endOdometer = 'End odometer must be greater than start odometer';
      hasOdometerErrors = true;
    }

    if (start > 0 && dropoff > 0 && start > dropoff) {
      odometerValidationErrors.startOdometer = 'Start odometer must be less than dropoff odometer';
      odometerValidationErrors.dropOffOdometer = 'Dropoff odometer must be greater than start odometer';
      hasOdometerErrors = true;
    }

    if (pickup > 0 && end > 0 && pickup > end) {
      odometerValidationErrors.pickUpOdometer = 'Pickup odometer must be less than end odometer';
      odometerValidationErrors.endOdometer = 'End odometer must be greater than pickup odometer';
      hasOdometerErrors = true;
    }
    if (hasOdometerErrors) {
      setOdometerErrors(odometerValidationErrors);
      setSnackbarMessage('Please correct the odometer reading errors before saving');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false);
      return;
    }

    // Clear any existing odometer errors if validation passes
    setOdometerErrors({
      startOdometer: '',
      pickUpOdometer: '',
      dropOffOdometer: '',
      endOdometer: '',
    });
      
      // Format datetime strings properly for API
      const formatDateTimeForAPI = (dateStr: string): string => {
        if (!dateStr) return '';
        // If it's already in ISO format, use it as is
        if (dateStr.includes('T')) {
          return dateStr.includes('.') ? dateStr.split('.')[0] : dateStr;
        }
        // Otherwise, assume it's from datetime-local input and append :00
        return dateStr.includes(':') && dateStr.split(':').length === 2 ? `${dateStr}:00` : dateStr;
      };

      // Create the update payload with the new API format
      // If in edit mode and travelServiceDetailId is not generated, use the id from trip.travelServiceDetail
      const travelServiceDetailId = isEditing && (!editingTrip.travelServiceDetailId || editingTrip.travelServiceDetailId === undefined)
        ? trip?.travelServiceDetail?.id
        : editingTrip.travelServiceDetailId;

      const shouldIncludeEndDatetime = !shouldHideEndDate();

      const updatePayload = {
        customerId: customer?.id || 1,
        travelServiceDetailId: Number(travelServiceDetailId),
        sourceCity: editingTrip.sourceCity,
        destinationCities: editingTrip.destinationCities,
        startDatetime: formatDateTimeForAPI(editingTrip.startDatetime),
        // endDatetime: formatDateTimeForAPI(editingTrip.endDatetime || ""),
        endDatetime: shouldIncludeEndDatetime 
      ? formatDateTimeForAPI(editingTrip.endDatetime || "") 
      : formatDateTimeForAPI(editingTrip.startDatetime), // Use start date for single-day services
        request: editingTrip.request || "",
        notes: editingTrip.notes || "",
        vendorGst: editingTrip.vendorGst || "",
        startOdometer:  editingTrip.startOdometer === null || editingTrip.startOdometer === undefined ? 0 : Number(editingTrip.startOdometer),
        endOdometer:  editingTrip.endOdometer === null || editingTrip.endOdometer === undefined ? 0 : Number(editingTrip.endOdometer),
        pickUpOdometer:  editingTrip.pickUpOdometer === null || editingTrip.pickUpOdometer === undefined ? 0 : Number(editingTrip.pickUpOdometer),
        dropOffOdometer:  editingTrip.dropOffOdometer === null || editingTrip.dropOffOdometer === undefined ? 0 : Number(editingTrip.dropOffOdometer),
        linkedTripCode: linkedTripCodeValue,
        pricing: {
          noOfDays: calculatedDays,
          ratePerDay: selectedFleetOption?.fleetDetails?.offerPrice,
          ratePerExtraKm: selectedFleetOption?.fleetDetails?.extraKmCharge,
          ratePerExtraHour: selectedFleetOption?.fleetDetails?.extraHourCharge,
          driverAllowancePerDay: selectedFleetOption?.fleetDetails?.driverAllowance,
          agreedTripFare: agreedPrice || 0,
          estimatedTripFare: (calculatedDays * (selectedFleetOption?.fleetDetails?.offerPrice)) + (calculatedDays * (selectedFleetOption?.fleetDetails?.driverAllowance)),
          discount: estimatedPrice - agreedPrice
        }
        
      };

      // Call the update function and wait for completion
      await onUpdate(updatePayload);

      // Exit editing mode
      setIsEditing(false);

      // Show success message
      setSnackbarMessage('Trip information updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

    } catch (error: any) {
      console.error('Error updating trip information:', error);
      setSnackbarMessage(error.message || 'Failed to update trip information');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSaving(false);
    }
  };

  // Calculate days between start and end date
  const calculateDays = (startDate: string, endDate?: string): number => {
    if (!startDate || !endDate) return 1;

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate difference in days, with a minimum of 1 day
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(diffDays, 1);
  };

  // Handler for discounted price change from TripPricingCalculator
  const handleDiscountedPriceChange = (discountedPrice: number) => {
    setCurrentDiscountedPrice(discountedPrice);
  };

  // Handler for distance-based price calculation change from TripPricingCalculator
  const handleDistancePriceCalculationChange = (price: number | null) => {
    setDistancePriceCalculation(price);
  };

  // Compute the default agreed price based on the priority:
  // 1. If distancePriceCalculation is available, use it
  // 2. If not, use currentDiscountedPrice
  // 3. Return null if no calculated price available
  const getCalculatedPrice = () => {
    // Priority 1: distancePriceCalculation if available and is a valid number
    if (distancePriceCalculation !== null &&
      distancePriceCalculation !== undefined &&
      typeof distancePriceCalculation === 'number' &&
      !isNaN(distancePriceCalculation) &&
      distancePriceCalculation > 0) {
      return distancePriceCalculation;
    }

    // Priority 2: currentDiscountedPrice if available and is a valid number
    if (currentDiscountedPrice !== null &&
      currentDiscountedPrice !== undefined &&
      typeof currentDiscountedPrice === 'number' &&
      !isNaN(currentDiscountedPrice) &&
      currentDiscountedPrice > 0) {
      return currentDiscountedPrice;
    }

    // Default: no calculated price available
    return null;
  };

  const currentTrip = isEditing ? editingTrip : trip;

  // Helper function to get service name for display
  const getServiceName = () => {
    // If we have pricing table data and trip ID, calculate from that
    if (trip?.travelServiceDetailId && pricingTable?.data) {
      const travelServiceDetailId = Number(trip.travelServiceDetailId);
      const serviceIndex = Math.floor((travelServiceDetailId - 1) / 4);
      const service = pricingTable.data[serviceIndex];
      if (service?.serviceName) {
        return service.serviceName;
      }
    }

    // Fallback to trip data
    if (trip?.travelServiceDetail?.travelService?.name) {
      return trip.travelServiceDetail.travelService.name;
    }

    return trip?.travelServiceDetail?.serviceType || 'N/A';
  };

  // Helper function to get vehicle name for display
  const getVehicleName = () => {
    // If we have pricing table data and trip ID, calculate from that
    if (trip?.travelServiceDetailId && pricingTable?.data) {
      const travelServiceDetailId = Number(trip.travelServiceDetailId);
      const serviceIndex = Math.floor((travelServiceDetailId - 1) / 4);
      const fleetIndex = ((travelServiceDetailId - 1) % 4);

      const service = pricingTable.data[serviceIndex];
      if (service?.carOptions && service.carOptions[fleetIndex]) {
        const fleet = service.carOptions[fleetIndex];
        const carType = fleet.carType || 'Vehicle';
        const makeModel = fleet.makeModel || fleet.makeAndModel || fleet.model || fleet.name || 'Model';
        const capacity = fleet.capacity || 'N/A';
        const slot = fleet.slot;

        // Use the same format as the dropdown
        return slot && slot !== ''
          ? `${carType} | ${makeModel} | ${capacity} (${slot})`
          : `${carType} | ${makeModel} | ${capacity}`;
      }
    }

    // Fallback to trip data with improved formatting
    if (trip?.travelServiceDetail) {
      const { carType, makeModel, slot, carTypeName } = trip.travelServiceDetail;
      // If we have a pre-formatted carTypeName, use it
      if (carTypeName && carTypeName.includes('|')) {
        return carTypeName;
      }

      // Otherwise, construct the name in the same format as dropdown
      const carTypeName_display = typeof carType === 'object' ? carType?.carType : (carType || carTypeName || 'Vehicle');
      const makeModel_display = makeModel || (typeof carType === 'object' ? carType?.model : undefined) || 'Model';
      const capacity_display = trip.travelServiceDetail?.maxPassengers || 'N/A'; // Add fallback for undefined capacity

      const displayName = `${carTypeName_display} | ${capacity_display}`;
      return slot && slot !== '' ? `${displayName} (${slot})` : displayName;
    }

    return 'N/A';
  };

  // Load pricing table data when component mounts for display purposes
  useEffect(() => {
    const loadPricingTableForDisplay = async () => {
      if (!pricingTable) {
        try {
          const data = await api.getPricingTable() as any;
          setPricingTable(data);
        } catch (error) {
          console.error('Error loading pricing table for display:', error);
        }
      }
    };

    loadPricingTableForDisplay();
  }, []);

  useEffect(()=>{
    if(isEditing && (!editingTrip?.request || editingTrip?.request==='')){
      setEditingTrip(prev=>({
        ...prev,
        request: 'onotravels'
      }))
    }
  },[isEditing])

  return (
    <>
      <Card ref={tripInfoSectionRef} sx={cardSx}>
        <TripInformationHeader
          isEditing={isEditing}
          loading={loading}
          customer={customer}
          disabled={disabled}
          isInProgress={isInProgress}
          isCompleted={isCompleted}
          onModify={handleModify}
          onOpenWhatsAppDialog={handleOpenWhatsAppDialog}
          onOpenReminderDialog={handleOpenReminderDialog}
        />
        <CardContent sx={cardContentSx}>
          <Box sx={flexWrapBoxSx}>
            <Box sx={fieldBoxSx}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Trip Code
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="white">
                #{currentTrip?.tripCode}
              </Typography>
            </Box>

            {/* Current Vehicle Type - Only show in edit mode */}
            {isEditing && (
              <Box sx={{
                flex: '1 1 300px',
                minWidth: 250,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
                borderRadius: 1,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                  <InfoIcon sx={{ fontSize: 16, color: '#2196f3' }} />
                  <Typography variant="body2" color="#2196f3" fontWeight="medium">
                    Previously Selected Vehicle
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="white"
                  sx={{
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    opacity: 0.9
                  }}
                >
                  {getVehicleName()}
                </Typography>
              </Box>
            )}

            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Service Type
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedServiceId || ''}
                    onChange={(e) => handleServiceChange(e.target.value as string)}
                    displayEmpty
                    disabled={loadingServices || disabled || isInProgress || isCompleted}
                    sx={selectSx}
                    MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                    renderValue={(selected) => {

                      if (selected && serviceOptions.length > 0) {
                        // Show selected service name
                        const selectedService = serviceOptions.find(s => s.id === Number(selected));
                        if (selectedService) {
                          return selectedService.name;
                        }
                      }

                      // Show loading text
                      if (loadingServices) {
                        return 'Loading services...';
                      }

                      // If we have service options but no valid selection, try to find by current service name
                      if (serviceOptions.length > 0) {
                        const currentServiceName = getServiceName();
                        const serviceByName = serviceOptions.find(s =>
                          s.name?.toLowerCase().trim() === currentServiceName?.toLowerCase().trim()
                        );

                        if (serviceByName) {
                          // Auto-select the service if found by name but not yet selected
                          setTimeout(() => setSelectedServiceId(serviceByName.id), 0);
                          return serviceByName.name;
                        }

                        return 'Select Service Type';
                      }

                      // Fallback to current service name only when services aren't loaded yet
                      const fallbackName = getServiceName();
                      return fallbackName;
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingServices ? 'Loading services...' : 'Select Service Type'}
                    </MenuItem>
                    {serviceOptions.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {getServiceName()}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Vehicle Type
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedFleetId}
                    onChange={(e) => handleFleetChange(e.target.value as string)}
                    displayEmpty
                    disabled={!selectedServiceId || fleetOptions.length === 0 || disabled || isInProgress || isCompleted}
                    sx={selectSx}
                    MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                  >
                    <MenuItem value="" disabled>
                      {!selectedServiceId ? 'Select service first' : 'Select Vehicle Type'}
                    </MenuItem>
                    {fleetOptions.map((fleet) => (
                      <MenuItem key={fleet.id} value={fleet.id}>
                        {fleet.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {getVehicleName()}
                </Typography>
              )}
            </Box>

            {/* Mumbai Airport Pickup/Drop Selection */}
            {isEditing && isMumbaiAirportService && (
              <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Pickup or Drop
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={airportPickupDrop}
                    onChange={(e) => handleAirportPickupDropChange(e.target.value as string)}
                    displayEmpty
                    disabled={disabled || isInProgress || isCompleted}
                    sx={selectSx}
                    MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                  >
                    <MenuItem value="" disabled>
                      Select Pickup or Drop
                    </MenuItem>
                    <MenuItem value="pickup">
                      Pickup from Mumbai Airport → Pune
                    </MenuItem>
                    <MenuItem value="drop">
                      Drop to Mumbai Airport ← Pune
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Oneway Route Selection */}
            {isEditing && (isOnewayService || isPerPaxService) && (
              <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Route Direction
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={onewayRoute || perpaxRoute}
                    onChange={(e) => handleRouteChange(e.target.value as string)}
                    displayEmpty
                    disabled={disabled || isInProgress || isCompleted}
                    sx={selectSx}
                    MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                  >
                    <MenuItem value="" disabled>
                      Select Route Direction
                    </MenuItem>
                    <MenuItem value="pune-to-mumbai">
                      Pune → Mumbai
                    </MenuItem>
                    <MenuItem value="mumbai-to-pune">
                      Mumbai → Pune
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}



            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                From
              </Typography>
              {isEditing ? (
                <>
                  {/* Show dropdown for Outstation Round Trip service */}
                  {(() => {
                    const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
                    const serviceName = selectedService?.name?.toLowerCase() || '';
                    const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

                    return isOutstationRoundTrip ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={editingTrip?.sourceCity || ''}
                          onChange={(e) => handleInputChange('sourceCity', e.target.value)}
                          displayEmpty
                          disabled={disabled || isInProgress || isCompleted}
                          sx={selectSx}
                          MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                          renderValue={(selected) => {
                            if (selected) {
                              return selected;
                            }
                            return 'Select starting city';
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select starting city
                          </MenuItem>
                          <MenuItem value="Pune">
                            Pune
                          </MenuItem>
                          <MenuItem value="Mumbai">
                            Mumbai
                          </MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        value={editingTrip?.sourceCity || ''}
                        onChange={(e) => handleInputChange('sourceCity', e.target.value)}
                        variant="outlined"
                        size="small"
                        disabled={disabled || isInProgress || isCompleted}
                        InputProps={{
                          readOnly: isMumbaiAirportService || isOnewayService || isPerPaxService || isPuneLocalService,
                        }}
                        placeholder={
                          isMumbaiAirportService ? "Will be prefilled based on pickup/drop selection" :
                            isOnewayService ? "Will be prefilled based on route selection" :
                              isPuneLocalService ? "Will be prefilled as Pune for local service" :
                                "Enter source city"
                        }
                        sx={{
                          ...textFieldSx,
                          ...(isMumbaiAirportService || isOnewayService || isPerPaxService || isPuneLocalService ? {
                            '& .MuiInputBase-input': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.7)',
                              cursor: 'not-allowed'
                            }
                          } : {})
                        }}
                      />
                    );
                  })()}
                </>
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.sourceCity || 'N/A'}
                </Typography>
              )}
            </Box>

            {/* Conditionally show/hide To field based on service type */}
            {!shouldHideToField() && (
              <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  To
                </Typography>
                {isEditing ? (
                  <Box>
                    {/* Google Places and Add button only for Outstation Round Trip */}
                    {(() => {
                      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
                      const serviceName = selectedService?.name?.toLowerCase() || '';
                      const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

                      return isOutstationRoundTrip && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Autocomplete
                              freeSolo
                              options={placePredictions}
                              loading={isLoadingPredictions}
                              disabled={isInProgress || isCompleted}
                              getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.description
                              }
                              filterOptions={(x) => x} // Disable built-in filtering
                              inputValue={newDestination}
                              onInputChange={(event, value) => {
                                setNewDestination(value || '');
                              }}
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  const selectedPlace = typeof newValue === 'string'
                                    ? newValue
                                    : newValue.description;

                                  setNewDestination(selectedPlace);
                                  resetSessionToken(); // Reset session token after selection
                                }
                              }}
                              renderOption={(props, option) => (
                                <li {...props} key={option.place_id}>
                                  <Box>
                                    <Typography variant="body1" component="span">
                                      {option.structured_formatting?.main_text || option.description}
                                    </Typography>
                                    {option.structured_formatting?.secondary_text && (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}
                                      >
                                        {option.structured_formatting.secondary_text}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  size="small"
                                  placeholder="Search for a destination city"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {isLoadingPredictions ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                  sx={textFieldSx}
                                />
                              )}
                            />
                          </Box>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!newDestination}
                            onClick={handleAddDestination}
                            sx={{
                              minWidth: '40px',
                              height: '40px',
                              backgroundColor: '#3b82f6',
                              '&:hover': { backgroundColor: '#2563eb' }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </Button>
                        </Box>
                      );
                    })()}

                    {/* Simple text field for all other services */}
                    {(() => {
                      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
                      const serviceName = selectedService?.name?.toLowerCase() || '';
                      const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

                      return !isOutstationRoundTrip && (
                        <TextField
                          fullWidth
                          value={editingTrip?.destinationCities || ''}
                          onChange={(e) => handleInputChange('destinationCities', e.target.value)}
                          variant="outlined"
                          size="small"
                          disabled={disabled || isInProgress || isCompleted}
                          InputProps={{
                            readOnly: isMumbaiAirportService || isOnewayService || isPerPaxService,
                          }}
                          placeholder={
                            isMumbaiAirportService ? "Will be prefilled based on pickup/drop selection" :
                              isOnewayService ? "Will be prefilled based on route selection" : isPerPaxService ? "Will be prefilled based on route selection" :
                                "Enter destination cities"
                          }
                          sx={{
                            ...textFieldSx,
                            ...(isMumbaiAirportService || isOnewayService || isPerPaxService ? {
                              '& .MuiInputBase-input': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                cursor: 'not-allowed'
                              }
                            } : {})
                          }}
                        />
                      );
                    })()}

                    {/* Show destination chips - only for Outstation Round Trip */}
                    {(() => {
                      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
                      const serviceName = selectedService?.name?.toLowerCase() || '';
                      const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

                      return destinationsList.length > 0 && isOutstationRoundTrip && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {destinationsWithDistance.map((destination, index) => (
                            <div
                              key={index}
                              draggable
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragOver={handleDragOver}
                              onDragEnd={handleDragEnd}
                              onDrop={(e) => handleDrop(e, index)}
                              onDragEnter={handleDragEnter}
                              onDragLeave={handleDragLeave}
                              style={{
                                cursor: 'grab'
                              }}
                            >
                              <Chip
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: '50%',
                                      backgroundColor: '#3b82f6',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}>
                                      {index + 1}
                                    </Box>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                      {destination.name}
                                      {destination.isCalculating && (
                                        <CircularProgress
                                          size={8}
                                          sx={{ ml: 0.5, verticalAlign: 'middle' }}
                                        />
                                      )}
                                      {!destination.isCalculating && destination.distance !== null && (
                                        <Typography
                                          component="span"
                                          sx={{
                                            ml: 0.5,
                                            fontSize: '0.7rem',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          ({destination.distance} km)
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Box>
                                }
                                onDelete={() => handleRemoveDestination(index)}
                                color="primary"
                                variant="outlined"
                                disabled={ isInProgress || isCompleted}
                                size="small"
                                sx={{
                                  backgroundColor: '#1e293b',
                                  color: 'white',
                                  borderColor: '#3b82f6',
                                  '& .MuiChip-deleteIcon': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    '&:hover': {
                                      color: 'white'
                                    }
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </Box>
                      );
                    })()}

                    {/* Total distance display - only for Outstation Round Trip */}
                    {(() => {
                      const selectedService = serviceOptions.find(s => s.id === Number(selectedServiceId));
                      const serviceName = selectedService?.name?.toLowerCase() || '';
                      const isOutstationRoundTrip = serviceName.includes('outstation') && serviceName.includes('round trip');

                      return totalDistance !== null && destinationsWithDistance.length > 0 && isOutstationRoundTrip && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.75rem',
                              fontStyle: 'italic'
                            }}
                          >
                            Total route distance: <strong>{totalDistance} km</strong>
                          </Typography>
                        </Box>
                      );
                    })()}

                    {/* Hidden field to maintain form compatibility */}
                    <input
                      type="hidden"
                      name="destinationCities"
                      value={destinationsList.join(' | ')}
                    />
                  </Box>
                ) : (
                  <Typography variant="body1" fontWeight="medium" color="white">
                    {currentTrip?.destinationCities || 'N/A'}
                  </Typography>
                )}
              </Box>
            )}


            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Start Date Time
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="datetime-local"
                  value={editingTrip?.startDatetime?.slice(0, 16) || ''}
                  onChange={(e) => handleInputChange('startDatetime', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={disabled || isInProgress || isCompleted}
                  sx={dateFieldSx}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {formatDate(currentTrip?.startDatetime || '')}
                </Typography>
              )}
            </Box>

            {/* Only show end date for services that need it */}
            {!shouldHideEndDate() && (
              <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  End Date Time
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={editingTrip?.endDatetime?.slice(0, 16) || ''}
                    onChange={(e) => handleInputChange('endDatetime', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    sx={dateFieldSx}
                    inputProps={{ min: editingTrip?.startDatetime ? editingTrip.startDatetime.slice(0, 16) : undefined }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="medium" color="white">
                    {formatDate(currentTrip?.endDatetime || '')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Lead Source Field */}
            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Lead Source
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editingTrip?.request || ''}
                    onChange={(e) => handleInputChange('request', e.target.value)}
                    displayEmpty
                    disabled={disabled || isInProgress || isCompleted}
                    sx={selectSx}
                    MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                  >
                    <MenuItem value="" disabled>
                      Select Lead Source
                    </MenuItem>
                    <MenuItem value="onotravels">onotravels</MenuItem>
                    <MenuItem value="Google">Google</MenuItem>
                    <MenuItem value="Justdial">Justdial</MenuItem>
                    <MenuItem value="Reference">Reference</MenuItem>
                    <MenuItem value="Existing Customer">Existing Customer</MenuItem>
                    <MenuItem value="Others">Others</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.request || 'N/A'}
                </Typography>
              )}
            </Box>

            {/* GST Field */}
            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                GST Number
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  value={editingTrip?.vendorGst || ''}
                  onChange={(e) => handleInputChange('vendorGst', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  placeholder="Enter GST number (optional)"
                  sx={textFieldSx}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.vendorGst || 'N/A'}
                </Typography>
              )}

            </Box>
             {(() => {
              // Check if it's Per Pax service in edit mode
              if (isEditing && isPerPaxService) {
                return true;
              }
              
              // Check if it's Per Pax service in non-edit mode
              if (!isEditing && trip?.travelServiceDetail) {
                const serviceName = trip.travelServiceDetail.travelService?.name?.toLowerCase() || '';
                const serviceType = trip.travelServiceDetail.serviceType?.toLowerCase() || '';
                return serviceName.includes('per pax') || serviceType.includes('per pax');
              }
              
              return false;
            })()&& (
            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Linked Trip Code
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  value={editingTrip?.linkedTripCode || ''}
                  onChange={(e) => handleInputChange("linkedTripCode", e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={disabled || isInProgress || isCompleted}
                  placeholder="Enter Linked Trip Code (optional)"
                  sx={textFieldSx}
                  inputProps={{ maxLength: 6 }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.linkedTripCode || 'N/A'}
                </Typography>
              )}
            </Box>
            )}

            {/* Notes Field */}
            <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Additional Notes
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={editingTrip?.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={disabled || isInProgress || isCompleted}
                  placeholder="Enter additional notes"
                  sx={textFieldSx}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white" sx={{ whiteSpace: 'pre-wrap'}}>
                  {currentTrip?.notes || 'N/A'}
                </Typography>
              )}
            </Box>

            {isClosed && (<Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Zoho Invoice Id
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="warning" sx={{ whiteSpace: 'pre-wrap'}}>
                  {currentTrip?.zohoInvoiceId || "Contact technical team to create Zoho Invoice" }
              </Typography>
              </Box>)}

            <CardContent sx={{ backgroundColor: '#444444ff', color: 'white', borderTop: '1px solid #444', display: 'flex', flexWrap: 'wrap', gap: 1, pt: 3,width: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ width: '100%' }}>
                Odometer Readings (in km)
              </Typography>
              {/* Start Odometer Field */}
            <Box sx={{ flex: '1 1 150px', minWidth: 120, maxWidth: 190 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Start
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="number"
                  value={editingTrip?.startOdometer || ''}
                  onChange={(e) => handleInputChange('startOdometer', e.target.value ? Number(e.target.value) : 0)}
                  error={!!odometerErrors.startOdometer}
                  helperText={!!odometerErrors.startOdometer}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  placeholder="Enter start odometer reading"
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  sx={textFieldSx}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.startOdometer ? `${currentTrip.startOdometer}` : 'N/A'}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: 120, maxWidth: 190 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                PickUp
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="number"
                  value={editingTrip?.pickUpOdometer || ''}
                  onChange={(e) => handleInputChange('pickUpOdometer', e.target.value ? Number(e.target.value) : 0)}
                  error={!!odometerErrors.pickUpOdometer}
                  helperText={odometerErrors.pickUpOdometer}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  placeholder="Enter pick up odometer reading"
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  sx={textFieldSx}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.pickUpOdometer ? `${currentTrip.pickUpOdometer}` : 'N/A'}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: 120, maxWidth: 190 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                DropOff
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="number"
                  value={editingTrip?.dropOffOdometer || ''}
                  onChange={(e) => handleInputChange('dropOffOdometer', e.target.value ? Number(e.target.value) : 0)}
                  error={!!odometerErrors.dropOffOdometer}
                  helperText={odometerErrors.dropOffOdometer}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  placeholder="Enter drop off odometer reading"
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  sx={textFieldSx}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.dropOffOdometer ? `${currentTrip.dropOffOdometer}` : 'N/A'}
                </Typography>
              )}
            </Box>

            {/* End Odometer Field */}
            <Box sx={{ flex: '1 1 150px', minWidth: 120, maxWidth: 190 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                End
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="number"
                  value={editingTrip?.endOdometer || ''}
                  onChange={(e) => handleInputChange('endOdometer', e.target.value ? Number(e.target.value) : 0)}
                  error={!!odometerErrors.endOdometer}
                  helperText={odometerErrors.endOdometer}
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  placeholder="Enter end odometer reading"
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  sx={textFieldSx}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium" color="white">
                  {currentTrip?.endOdometer ? `${currentTrip.endOdometer}` : 'N/A'}
                </Typography>
              )}
            </Box>
            </CardContent>
            {/* </Box> */}
          </Box>
        </CardContent>

        {/* Fare Breakup Section */}
        {((isEditing && selectedServiceId && selectedFleetId && fleetOptions.length > 0) ||
          (!isEditing && trip?.travelServiceDetailId && pricingTable?.data)) && (
            <CardContent sx={{ backgroundColor: '#2a2a2a', color: 'white', borderTop: '1px solid #444', pt: 3 }}>
              <Typography variant="h6" color="white" sx={{ mb: 2 }}>
                Fare Breakup
              </Typography>

              {/* Fare calculation content */}
              {isEditing ? (
                // Editing mode fare calculation
                (() => {
                  const selectedServiceOption = serviceOptions.find(s => s.id === Number(selectedServiceId));
                  const selectedFleetOption = fleetOptions.find(f => f.id === Number(selectedFleetId));

                  if (selectedServiceOption && selectedFleetOption?.fleetDetails) {
                    const serviceName = selectedServiceOption.name.toUpperCase();
                    let serviceType = 'LOCAL';

                    if (serviceName.includes('AIRPORT')) {
                      serviceType = 'AIRPORT';
                    } else if (serviceName.includes('ONE')) {
                      serviceType = 'ONEWAY';
                    } else if (serviceName.includes('OUTSTATION')) {
                      serviceType = 'OUTSTATION';
                    }

                    return (
                      <TripPricingCalculator
                        selectedFleetInfo={selectedFleetOption.fleetDetails}
                        serviceType={serviceType}
                        calculatedDays={calculatedDays}
                        customKm={customKm}
                        totalDistance={totalDistance}
                        onDiscountedPriceChange={handleDiscountedPriceChange}
                        onDistancePriceCalculationChange={handleDistancePriceCalculationChange}
                      />
                    );
                  }
                  return null;
                })()
              ) : (
                // View mode fare calculation
                (() => {
                  const travelServiceDetailId = Number(trip.travelServiceDetailId);
                  const serviceIndex = Math.floor((travelServiceDetailId - 1) / 4);
                  const fleetIndex = ((travelServiceDetailId - 1) % 4);

                  const service = pricingTable.data[serviceIndex];
                  if (service?.carOptions && service.carOptions[fleetIndex]) {
                    const fleet = service.carOptions[fleetIndex];
                    const serviceName = service.serviceName || '';


                    let serviceType = 'LOCAL';
                    if (serviceName.toUpperCase().includes('AIRPORT')) {
                      serviceType = 'AIRPORT';
                    } else if (serviceName.toUpperCase().includes('OUTSTATION')) {
                      serviceType = 'OUTSTATION';
                    } else if (serviceName.toUpperCase().includes('ONEWAY') || serviceName.toUpperCase().includes('ONE WAY')) {
                      serviceType = 'ONEWAY';
                    }

                    const days = calculateDays(trip.startDatetime, trip.endDatetime);
                    return (
                      <TripPricingCalculator
                        selectedFleetInfo={fleet}
                        serviceType={serviceType}
                        calculatedDays={days}
                        customKm={0}
                        totalDistance={totalDistance}
                        onDiscountedPriceChange={handleDiscountedPriceChange}
                        onDistancePriceCalculationChange={handleDistancePriceCalculationChange}
                      />
                    );
                  }
                  return null;
                })()
              )}
            </CardContent>
          )}

        {/* Agreed Price Section - Only show when editing */}
        {isEditing && (
          <CardContent sx={{ backgroundColor: '#161616', color: 'white', borderTop: '1px solid #444', pt: 3 }}>
            <Typography variant="h6" color="white" sx={{ mb: 2 }}>
              Pricing Information
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 250, maxWidth: 400 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Agreed Trip Fare
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder={(() => {
                    const calculatedPrice = getCalculatedPrice();
                    return calculatedPrice !== null ? `Calculated Price: ₹${calculatedPrice.toLocaleString('en-IN')}` : "Enter agreed fare amount";
                  })()}
                  value={(() => {
                    // If user has manually entered a price, always use that
                    if (hasUserEditedPrice && (agreedPrice !== null && agreedPrice !== undefined)) {
                      return agreedPrice;
                    }

                    // If user hasn't edited and we have a user-entered price, use that
                    if (agreedPrice !== null && agreedPrice !== undefined) {
                      return agreedPrice;
                    }

                    // If no user price and user hasn't edited, show calculated price for pre-fill
                    if (!hasUserEditedPrice) {
                      const calculatedPrice = getCalculatedPrice();
                      if (calculatedPrice !== null) {
                        return calculatedPrice;
                      }
                    }

                    // Default to empty
                    return '';
                  })()}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Mark that user has manually edited the price
                    setHasUserEditedPrice(true);

                    if (value === '') {
                      setAgreedPrice(null);
                    } else {
                      const numValue = Number(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setAgreedPrice(numValue);
                      }
                    }
                  }}
                  onFocus={() => {
                    // When user focuses on the field, if it shows calculated price, 
                    // mark as user edited so they can modify it
                    const calculatedPrice = getCalculatedPrice();
                    if (!hasUserEditedPrice && calculatedPrice !== null &&
                      (agreedPrice === null || agreedPrice === undefined)) {
                      // Pre-fill the agreedPrice with calculated price so user can edit it
                      setAgreedPrice(calculatedPrice);
                      setHasUserEditedPrice(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent arrow up/down from incrementing/decrementing
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                    }
                  }}
                  variant="outlined"
                  size="small"
                  disabled={disabled || isCompleted}
                  InputProps={{
                    startAdornment: <span style={{ color: '#2196f3', marginRight: '4px', fontWeight: 'bold' }}>₹</span>,
                    inputProps: { min: 0 }
                  }}
                  sx={agreedPriceFieldSx}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              </Box>
            </Box>
          </CardContent>
        )}

        {/* Action Buttons - Fixed Position at Bottom */}
        {isEditing && (
          <CardContent sx={actionButtonsSx}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ color: 'white', borderColor: 'white' }}
              disabled={saving || disabled}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ backgroundColor: '#3b82f6' }}
              disabled={saving || disabled}
            >
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* WhatsApp Notification Dialog */}
      <Dialog
        open={openWhatsAppDialog}
        onClose={handleCloseWhatsAppDialog}
        aria-labelledby="whatsapp-dialog-title"
      >
        <DialogTitle id="whatsapp-dialog-title" sx={{ bgcolor: '#075E54', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon /> Send Trip Confirmation via WhatsApp
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: '#1e293b', color: 'white' }}>
          <DialogContentText sx={{ color: '#e2e8f0' }}>
            Send a WhatsApp notification to {customer?.name || 'the customer'} at {customer?.mobileNumber || ''} with trip confirmation details.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">Notification Preview:</Typography>
            <Box sx={{ mt: 1, p: 2, bgcolor: '#2d3748', borderRadius: 1 }}>
              <Typography variant="body2">Trip Code: {trip.tripCode}</Typography>
              <Typography variant="body2">Customer: {customer?.name || 'N/A'}</Typography>
              <Typography variant="body2">Pickup: {trip.sourceCity || 'N/A'}</Typography>
              <Typography variant="body2">Drop: {trip.destinationCities || 'N/A'}</Typography>
              <Typography variant="body2">Date: {formatDate(trip.startDatetime).split(',')[0] || 'N/A'}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1e293b', color: 'white' }}>
          <Button onClick={handleCloseWhatsAppDialog} sx={{ color: 'white' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsAppNotification}
            variant="contained"
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
            disabled={sendingNotification}
            startIcon={sendingNotification ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {sendingNotification ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Reminder Dialog */}
      <Dialog
        open={openReminderDialog}
        onClose={handleCloseReminderDialog}
        aria-labelledby="reminder-dialog-title"
      >
        <DialogTitle id="reminder-dialog-title" sx={reminderDialogTitleSx}>
          <WhatsAppIcon /> Send Trip Reminder via WhatsApp
        </DialogTitle>
        <DialogContent sx={reminderDialogContentSx}>
          <DialogContentText sx={reminderDialogTextSx}>
            Send a WhatsApp reminder to {customer?.name || 'the customer'} at {customer?.mobileNumber || ''} about their upcoming trip.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">Reminder Preview:</Typography>
            <Box sx={{ mt: 1, p: 2, bgcolor: '#2d3748', borderRadius: 1 }}>
              <Typography variant="body2">
                Dear {customer?.name || 'Customer'}, this is a reminder for your upcoming trip:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Trip Code: {trip.tripCode}
              </Typography>
              <Typography variant="body2">Pickup: {trip.sourceCity || 'N/A'}</Typography>
              {/* <Typography variant="body2">Drop: {trip.destinationCities || 'N/A'}</Typography> */}
              <Typography variant="body2">Date: {formatDate(trip.startDatetime).split(',')[0] || 'N/A'}</Typography>
              {/* <Typography variant="body2">Time: {formatDate(trip.startDatetime).split(',')[1]?.trim() || 'N/A'}</Typography> */}
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Please be ready 15 minutes before pickup time. Have a safe journey!
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1e293b', color: 'white' }}>
          <Button onClick={handleCloseReminderDialog} sx={{ color: 'white' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsAppReminder}
            variant="contained"
            sx={{ bgcolor: '#FF9800', '&:hover': { bgcolor: '#F57C00' } }}
            disabled={sendingReminder}
            startIcon={sendingReminder ? <CircularProgress size={20} /> : <WhatsAppIcon />}
          >
            {sendingReminder ? 'Sending...' : 'Send Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TripInformationSection;